import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { ShippingMethodDTO } from '@invenco/shipping-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useQueryWithInput } from '@/shared/hooks/queries';

const formSchema = z.object({
  warehouseShippingMethodId: z.string().min(1, 'Please select a warehouse shipping method'),
});

type Props = {
  warehouseLocationId: string;
  existingShippingMethods: ShippingMethodDTO[];
  onSubmit: (warehouseShippingMethod: ShippingMethodDTO) => Promise<Result>;
};

export function ManagedShippingMethodDialogContent({
  warehouseLocationId,
  existingShippingMethods,
  onSubmit,
}: Props) {
  const { shippingGateway } = useGateways();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      warehouseShippingMethodId: '',
    },
  });

  useEffect(() => {
    form.reset({ warehouseShippingMethodId: '' });
  }, [form, warehouseLocationId]);

  const { data: warehouseShippingMethods = [], isLoading: isLoadingWarehouseShippingMethods } =
    useQueryWithInput({
      parentKey: 'shippingMethods',
      input: { locationId: warehouseLocationId },
      query: (input, { signal }) => shippingGateway.getShippingMethods(input, { signal }),
      select: ({ items }) => items,
      enabled: Boolean(warehouseLocationId),
    });

  const availableWarehouseShippingMethods = useMemo(() => {
    const existingIds = new Set(
      existingShippingMethods
        .map((shippingMethod) => shippingMethod.warehouseShippingMethodId)
        .filter(Boolean),
    );

    return warehouseShippingMethods.filter(
      (warehouseShippingMethod) =>
        Boolean(warehouseShippingMethod.id) && !existingIds.has(warehouseShippingMethod.id),
    );
  }, [existingShippingMethods, warehouseShippingMethods]);

  const hasAvailableMethods = availableWarehouseShippingMethods.length > 0;

  return (
    <FormDialogContent
      form={form}
      side="right"
      submitLabel="Add"
      isSubmitDisabled={!hasAvailableMethods}
      onSubmit={async ({ warehouseShippingMethodId }) => {
        const warehouseShippingMethod = availableWarehouseShippingMethods.find(
          (method) => method.id === warehouseShippingMethodId,
        );

        if (!warehouseShippingMethod) {
          return Result.fail(
            `Could not find warehouse shipping method with id ${warehouseShippingMethodId}`,
          );
        }

        if (warehouseShippingMethod.locationId !== warehouseLocationId) {
          return Result.fail(
            'Invalid warehouse shipping method selected for this managed location',
          );
        }

        return onSubmit(warehouseShippingMethod);
      }}
    >
      <DialogHeader>
        <DialogTitle>Add managed shipping method</DialogTitle>
        {!isLoadingWarehouseShippingMethods && !hasAvailableMethods ? (
          <DialogDescription>
            All available warehouse shipping methods have already been added
          </DialogDescription>
        ) : null}
      </DialogHeader>
      <DialogBody>
        <SelectFormField
          control={form.control}
          name="warehouseShippingMethodId"
          label="Warehouse shipping method"
          disabled={isLoadingWarehouseShippingMethods || !hasAvailableMethods}
          placeholder={
            isLoadingWarehouseShippingMethods
              ? 'Loading warehouse shipping methods...'
              : 'Select a warehouse shipping method'
          }
          className="w-full"
        >
          {availableWarehouseShippingMethods.map((warehouseShippingMethod) => (
            <SelectItem key={warehouseShippingMethod.id} value={warehouseShippingMethod.id}>
              {warehouseShippingMethod.name}
            </SelectItem>
          ))}
          {!availableWarehouseShippingMethods.length && !isLoadingWarehouseShippingMethods ? (
            <SelectItem value="no-warehouse-shipping-methods" disabled>
              No available warehouse shipping methods
            </SelectItem>
          ) : null}
        </SelectFormField>
      </DialogBody>
    </FormDialogContent>
  );
}
